import axios from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMytaverseEvent } from '@core/dashboard/providers';
import {
  convertVideoFileToWebm,
  parseAndValidateVideoFile,
} from '@core/dashboard/components/DashboardContent/CustomSpotsEditor/videoConverter';
import { useMytaverse } from '@providers/MytaverseProvider';
import BrandingsService, {
  ISpotLocalization,
  ISpotLocalizationsMap,
} from '@services/BrandingsService';
import FilesService from '@services/FilesService';
import { env } from 'env';

import { SpotMediaType } from './types';

export type LocalizationAssetField = 'media' | 'preview';

type AnySpot = Record<string, unknown> & {
  brandingSpotId?: string;
  roomSpotId?: string;
  localizations?: ISpotLocalizationsMap;
};

interface UseSpotLocalizationUploaderArgs {
  spot: AnySpot | null;
  mediaType: SpotMediaType;
}

interface UseSpotLocalizationUploaderResult {
  uploadingCodes: Set<string>;
  error: string | null;
  failures: Map<string, string>;
  uploadAsset: (
    code: string,
    field: LocalizationAssetField,
    file: File,
  ) => Promise<void>;
  removeLocalization: (code: string) => Promise<void>;
  clearPreview: (code: string) => Promise<void>;
}

const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];

const isAllowedImageFile = (file: File): boolean => {
  const mimeOk = !!file.type && ALLOWED_IMAGE_MIME_TYPES.includes(file.type);
  const nameOk = /\.(png|jpe?g)$/i.test((file.name ?? '').trim());
  return mimeOk || nameOk;
};

const mergeLocalizations = (
  current: ISpotLocalizationsMap | undefined,
  code: string,
  patch: Partial<ISpotLocalization> | null,
): ISpotLocalizationsMap => {
  const next: ISpotLocalizationsMap = { ...(current ?? {}) };
  if (patch === null) {
    delete next[code];
    return next;
  }
  next[code] = { ...(next[code] ?? {}), ...patch };
  return next;
};

export const useSpotLocalizationUploader = ({
  spot,
  mediaType,
}: UseSpotLocalizationUploaderArgs): UseSpotLocalizationUploaderResult => {
  const {
    currentBrandingId,
    isBrandingModeEnabled,
    currentRoom,
    setBrandingSpots,
    setRoomSpots,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { t } = useTranslation();

  const [uploadingCodes, setUploadingCodes] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<Map<string, string>>(new Map());

  const isBrandingSpot = Boolean(
    currentBrandingId && isBrandingModeEnabled && spot?.brandingSpotId,
  );

  const persist = useCallback(
    async (nextMap: ISpotLocalizationsMap) => {
      if (!spot) {
        return;
      }

      let updatedSpot: Record<string, unknown> | null = null;

      if (isBrandingSpot && currentBrandingId && spot.brandingSpotId) {
        updatedSpot = await BrandingsService.patchBrandingSpot(
          currentBrandingId,
          spot.brandingSpotId,
          { localizations: nextMap },
        );

        const updatedBrandingSpotId = spot.brandingSpotId;
        const persistedMap =
          (updatedSpot as { localizations?: ISpotLocalizationsMap } | null)
            ?.localizations ?? nextMap;
        setBrandingSpots((prev: unknown) => {
          const list = Array.isArray(prev)
            ? (prev as Record<string, unknown>[])
            : [];
          return list.map((s) =>
            s?.brandingSpotId === updatedBrandingSpotId
              ? { ...s, ...(updatedSpot ?? {}), localizations: persistedMap }
              : s,
          );
        });
      } else if (currentRoom && spot.roomSpotId) {
        const response = await axios.patch(
          `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${spot.roomSpotId}`,
          { localizations: nextMap },
        );
        updatedSpot =
          (response.data?.roomSpot as Record<string, unknown> | undefined) ??
          (response.data as Record<string, unknown> | undefined) ??
          null;

        const updatedRoomSpotId = spot.roomSpotId;
        const persistedMap =
          (updatedSpot as { localizations?: ISpotLocalizationsMap } | null)
            ?.localizations ?? nextMap;
        setRoomSpots((prev: unknown) => {
          const list = Array.isArray(prev)
            ? (prev as Record<string, unknown>[])
            : [];
          return list.map((s) =>
            s?.roomSpotId === updatedRoomSpotId
              ? { ...s, ...(updatedSpot ?? {}), localizations: persistedMap }
              : s,
          );
        });
      }

      if (updatedSpot) {
        sendMessageToUnreal({
          action: 'SPOTS_UPDATED',
          spots: [],
        });
      }
    },
    [
      spot,
      isBrandingSpot,
      currentBrandingId,
      currentRoom,
      setBrandingSpots,
      setRoomSpots,
      sendMessageToUnreal,
    ],
  );

  const runTracked = useCallback(
    async (trackingKey: string, action: () => Promise<void>) => {
      setUploadingCodes((prev) => {
        const next = new Set(prev);
        next.add(trackingKey);
        return next;
      });
      setFailures((prev) => {
        if (!prev.has(trackingKey)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(trackingKey);
        return next;
      });
      setError(null);

      try {
        await action();
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Localization update failed';
        console.error(`Failed to update spot localization: ${message}`, {
          brandingSpotId: spot?.brandingSpotId,
          roomSpotId: spot?.roomSpotId,
          code: trackingKey.split(':')[0],
        });
        setError(message);
        setFailures((prev) => {
          const next = new Map(prev);
          next.set(trackingKey, message);
          return next;
        });
      } finally {
        setUploadingCodes((prev) => {
          const next = new Set(prev);
          next.delete(trackingKey);
          return next;
        });
      }
    },
    [spot?.brandingSpotId, spot?.roomSpotId],
  );

  const uploadAsset = useCallback(
    (code: string, field: LocalizationAssetField, file: File) =>
      runTracked(`${code}:${field}`, async () => {
        const isVideoMedia = mediaType === 'video' && field === 'media';

        let fileToUpload = file;
        if (isVideoMedia) {
          const validation = parseAndValidateVideoFile(file);
          if (!validation.ok) {
            throw new Error(validation.message);
          }
          fileToUpload = await convertVideoFileToWebm(file);
        } else if (!isAllowedImageFile(file)) {
          throw new Error(t('customSpotsEditor.failureUnsupportedFormat'));
        }

        const [uploadInfo] = await FilesService.uploadFilesWithProgress([
          fileToUpload,
        ]);

        const patch: Partial<ISpotLocalization> =
          field === 'preview'
            ? { previewImage: uploadInfo.url }
            : { url: uploadInfo.url, location: uploadInfo.location };

        const nextMap = mergeLocalizations(spot?.localizations, code, patch);
        await persist(nextMap);
      }),
    [mediaType, spot?.localizations, persist, runTracked, t],
  );

  const removeLocalization = useCallback(
    (code: string) =>
      runTracked(code, async () => {
        const nextMap = mergeLocalizations(spot?.localizations, code, null);
        await persist(nextMap);
      }),
    [spot?.localizations, persist, runTracked],
  );

  const clearPreview = useCallback(
    (code: string) =>
      runTracked(`${code}:preview`, async () => {
        const current = spot?.localizations ?? {};
        const existing = current[code] ?? {};
        const cleared = { ...existing };
        delete cleared.previewImage;
        delete cleared.previewFilename;
        const nextMap: ISpotLocalizationsMap = {
          ...current,
          [code]: cleared,
        };
        await persist(nextMap);
      }),
    [spot?.localizations, persist, runTracked],
  );

  return {
    uploadingCodes,
    error,
    failures,
    uploadAsset,
    removeLocalization,
    clearPreview,
  };
};
